/**
 * IMPORTS
 */
import {
  Handshake,
  type LucideIcon,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  StickyNote,
  Users,
} from "lucide-react";

import type {
  ICompromissoAgenda,
  IStatusCompromisso,
  ITipoCompromisso,
} from "@/interface/agenda-cliente";

const TIPO_COMPROMISSO_INFO: Record<ITipoCompromisso, { label: string; Icone: LucideIcon }> = {
  nota: { label: "Nota", Icone: StickyNote },
  ligacao: { label: "Ligação", Icone: Phone },
  reuniao: { label: "Reunião", Icone: Users },
  email: { label: "E-mail", Icone: Mail },
  whatsapp: { label: "WhatsApp", Icone: MessageCircle },
  visita: { label: "Visita", Icone: MapPin },
  negociacao: { label: "Negociação", Icone: Handshake },
};

const STATUS_COMPROMISSO_INFO: Record<IStatusCompromisso, { label: string; classe: string }> = {
  pendente: { label: "Pendente", classe: "bg-amber-100 text-amber-800" },
  concluido: { label: "Concluído", classe: "bg-green-100 text-green-800" },
  cancelado: { label: "Cancelado", classe: "bg-gray-100 text-gray-600" },
};

/** Tipo desconhecido (enum novo na API) cai em "Nota" em vez de quebrar a tela. */
function obterInfoTipo(tipo: string) {
  return TIPO_COMPROMISSO_INFO[tipo as ITipoCompromisso] ?? TIPO_COMPROMISSO_INFO.nota;
}

function obterInfoStatus(status: string) {
  return (
    STATUS_COMPROMISSO_INFO[status as IStatusCompromisso] ?? {
      label: status,
      classe: "bg-gray-100 text-gray-600",
    }
  );
}

function ehMesmoDia(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Pendente agendado para hoje (no fuso do navegador). */
function ehCompromissoDeHoje(compromisso: ICompromissoAgenda, agora = new Date()): boolean {
  return (
    compromisso.status === "pendente" && ehMesmoDia(new Date(compromisso.agendado_para), agora)
  );
}

/** "Terça-feira, 21 de outubro de 2026" */
function formatarDataCompromisso(iso: string): string {
  const data = new Date(iso).toLocaleDateString("pt-BR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
  return data.charAt(0).toUpperCase() + data.slice(1);
}

/** "14:00" */
function formatarHoraCompromisso(iso: string): string {
  return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

/**
 * EXPORTS
 */
export {
  ehCompromissoDeHoje,
  formatarDataCompromisso,
  formatarHoraCompromisso,
  obterInfoStatus,
  obterInfoTipo,
};
